import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import Home from './components/Home';
import AuthLanding from './components/Auth/AuthLanding';
import StudentAuth from './components/Auth/StudentAuth';
import FacultyAuth from './components/Auth/FacultyAuth';
import AdminAuth from './components/Auth/AdminAuth';
import StudentDashboard from './components/Student/StudentDashboard';
import FacultyDashboard from './components/Faculty/FacultyDashboard';
import AdminDashboard from './components/Admin/AdminDashboard';
import About from './components/About/About';
import StudentRegister from './components/Auth/StudentRegister';
import EmailVerification from './components/Auth/EmailVerification';
import StudentSignIn from './components/Auth/StudentSignIn';
import FacultyRegister from './components/Auth/FacultyRegister';
import FacultySignIn from './components/Auth/FacultySignIn';
import AdminSignIn from './components/Auth/AdminSignIn';
import QRScanner from './components/Student/QRScanner';
import AttendEvent from './components/Student/AttendEvent';
import StudentProtectedRoute from './components/Auth/ProtectedRoute';

const theme = createTheme({
  palette: {
    primary: {
      main: '#DEA514',
      dark: '#B88A10',
    },
    secondary: {
      main: '#FFD700',
      dark: '#E6C200',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F5F5DC',
    },
    text: {
      primary: '#2C2C2C',
      secondary: '#B88A10',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: '#DEA514',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#B88A10',
          },
        },
        outlined: {
          borderColor: '#DEA514',
          color: '#DEA514',
          '&:hover': {
            borderColor: '#B88A10',
            color: '#B88A10',
            backgroundColor: 'rgba(222, 165, 20, 0.04)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#D66E00',
    },
    h2: {
      color: '#FF8C00',
    },
  },
});

const ProtectedRoute = ({ children, requiredUserType = null }) => {
  if (requiredUserType === "faculty") {
    const isFacultyAuthenticated = localStorage.getItem('facultyId') !== null;
    if (!isFacultyAuthenticated) return <Navigate to="/auth/faculty/signin" replace />;
  } else if (requiredUserType === "student") {
    const isStudentAuthenticated = localStorage.getItem('studentId') !== null;
    if (!isStudentAuthenticated) return <Navigate to="/auth/student/signin" replace />;
  } else if (requiredUserType === "admin") {
    const isAdminAuthenticated = localStorage.getItem('adminId') !== null;
    if (!isAdminAuthenticated) return <Navigate to="/auth/admin/signin" replace />;
  } else {
    const isAuthenticated = localStorage.getItem('facultyId') || localStorage.getItem('studentId') || localStorage.getItem('adminId');
    if (!isAuthenticated) return <Navigate to="/auth" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
   
        <Routes>
          <Route path="/verify-email" element={<EmailVerification />} />

          {/* Auth Routes */}
          <Route path="/auth" element={<><Navbar /><Outlet /></>}>
            <Route index element={<AuthLanding />} />
            <Route path="student" element={<StudentAuth />} />
            <Route path="faculty" element={<FacultyAuth />} />
            <Route path="admin" element={<AdminAuth />} />
            <Route path="student/signin" element={<StudentSignIn />} />
            <Route path="student/register" element={<StudentRegister />} />
            <Route path="faculty/signin" element={<FacultySignIn />} />
            <Route path="faculty/register" element={<FacultyRegister />} />
            <Route path="admin/signin" element={<AdminSignIn />} />
          </Route>

          {/* Faculty Dashboard */}
          <Route path="/faculty/*" element={
            <ProtectedRoute requiredUserType="faculty">
              <FacultyDashboard />
            </ProtectedRoute>
          } />

          {/* Admin Dashboard */}
          <Route path="/admin/*" element={
            <ProtectedRoute requiredUserType="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />

          {/* Main App Routes */}
          <Route path="/" element={<><Navbar /><Outlet /></>}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="student/dashboard" element={
              <ProtectedRoute requiredUserType="student">
                <StudentDashboard />
              </ProtectedRoute>
            } />
            <Route path="scan" element={
              <StudentProtectedRoute>
                <QRScanner />
              </StudentProtectedRoute>
            } />
            <Route path="attend/:eventId" element={
              <StudentProtectedRoute>
                <AttendEvent />
              </StudentProtectedRoute>
            } />
          </Route>
        </Routes>

    </ThemeProvider>
  );
}

export default App;
